/**
 * Browser-persisted session — so a returning player lands back in the room they
 * were in instead of the Splash/Home screen (owner request). We have no Colyseus
 * reconnection token on the server, so "rejoin" is a fresh `joinById(code)` into
 * the same GameRoom (a new player session); good enough for the cozy lobby.
 *
 * Persisted to `localStorage` (survives tab close / reload):
 *   - the player's identity (so the name/costume are remembered), and
 *   - the room they last ENTERED (code + name + optional private-room password).
 *
 * The room is remembered on join/create (`rememberRoom`) and forgotten on an
 * explicit Leave (`forgetRoom`) — so closing the tab keeps you in the room, but
 * deliberately leaving sends you home next time. Boot reads it via
 * `loadSavedSession()` and attempts the rejoin.
 */

import type { PlayerProfile } from '@ponponland/shared';
import { session } from './session.js';

const KEY = 'ponpon.session.v1';

interface SavedRoom {
  code: string;
  name: string;
  /** Stored so private rooms can be auto-rejoined; low-stakes casual game. */
  password?: string;
}

interface SavedSession {
  player: PlayerProfile;
  room: SavedRoom | null;
}

/** Last room password, kept so `rememberPlayer()` can re-persist losslessly. */
let savedPassword: string | undefined;

function write(room: SavedRoom | null): void {
  try {
    const data: SavedSession = { player: session.player, room };
    localStorage.setItem(KEY, JSON.stringify(data));
  } catch {
    /* private mode / quota / no storage — persistence is best-effort, never fatal */
  }
}

/**
 * Read the saved session, hydrate `session.player` (so the identity is
 * remembered), and return the saved room (if any) for Boot to rejoin. Returns
 * null on a first/cleared visit or any parse error.
 */
export function loadSavedSession(): SavedRoom | null {
  try {
    const raw = localStorage.getItem(KEY);
    if (!raw) return null;
    const v = JSON.parse(raw) as Partial<SavedSession>;
    if (!v || typeof v !== 'object' || !v.player || typeof v.player.name !== 'string') return null;
    session.player = v.player;
    const room = v.room ?? null;
    savedPassword = room?.password;
    return room;
  } catch {
    return null;
  }
}

/** Remember the room just entered (join/create) + identity. */
export function rememberRoom(code: string, name: string, password?: string): void {
  session.room = { code, name };
  savedPassword = password;
  write({ code, name, password });
}

/** Forget the room (explicit Leave) but keep the remembered identity. */
export function forgetRoom(): void {
  session.room = null;
  savedPassword = undefined;
  write(null);
}

/** Persist an identity change (Splash) without touching the remembered room. */
export function rememberPlayer(): void {
  write(session.room ? { code: session.room.code, name: session.room.name, password: savedPassword } : null);
}
